import * as React from 'react';
import { addItemToGoogleTracking, isNullOrWhitespace, isNullOrEmpty } from "../../utils";
import { IPageProperties } from "../../models";
import * as moment from "moment";
import { safe } from "../../utils";

const FA_ANGLE_DOWN = require('react-icons/lib/fa/angle-down');
const styles = require('./styles.pcss');

interface IPageCommentaryState {
    visible: boolean;
    limitTo: number;
}

export class PageCommentary extends React.Component<IPageProperties, IPageCommentaryState>{
    constructor(props) {
        super(props);

        this.state = { 
            visible: false, 
            limitTo: 1,
        };
        this.toggle = this.toggle.bind(this);
    }

    toggle() {
        let { shareClass, appParams } = this.props;
        let visible = this.state.limitTo === 1;
        addItemToGoogleTracking(appParams, "fund_page_monthly_fund_review", { eventCategory: shareClass.clientCode, eventLabel: visible ? 'read more' : 'read less' });
        
        const newLimit = this.state.limitTo === 1 ? -1 : 1;
        this.setState({ visible, limitTo: newLimit });
    }

    dangerHTMLWrapper(value) {
        return {
            __html: value
        };
    }

    /**
	 * Function used to get the selected shareclass commentary of a fund 
	 * based on the following rules:
	 * 
	 *  1. Display for Advisor (adv) & End Investor (pri) only
	 *  2. Commentary types must be displayed ascendingly in the order provided in the "order" field
	 *  3. Display for associated Investor Types provided in "clientType" data field
	 *  4. Only display Commentary types for isins provided in "isin" field
	 *  5. Each commentary type has an individual header provided in "heading"
	 *  6. Display commentary provided in "Commentary" field
	 * 
	 * Requirements: https://kurtosys-prod-eng.atlassian.net/wiki/spaces/MG/pages/1354008144/FD+105099+Replace+6+Month+Commentary+with+Monthly+Commentary
	 *
	 * @param {*} shareClass the selected share class
	 * @returns {any[]} an array of commentary objects
	 * @memberof PageCommentary
	 */
	getMonthlyFundCommentary(shareClass, appParams){
		let clientType = safe(() => appParams.clienttype, null);
        let commentaries = [];
        let excludedCommentaries = ['sixmonthcommentary'];
        // Display rule #1 & #3
        let clientTypesAllowed = ['adv','pri'];
        // Display rule #4
        if (shareClass.commentaries){
            for (let key in shareClass.commentaries){

                for (let item of shareClass.commentaries[key]){
                    if ((excludedCommentaries.indexOf(key) < 0) && (clientTypesAllowed.indexOf(item.clientType) > -1) && (item.clientType === clientType)){

						if (!item.is_deleted) {
							// Orchestrate commentary 
							if ( typeof item.commentary === 'string') {
								item.commentary = item.commentary.split("[SEPERATOR]");
							}

							// Check if commentary is empty or all elements are empty strings
							// (IE: item.commentary becomes a string array)
							const isBlankCommentary = isNullOrEmpty(item.commentary) || item.commentary.every((commentaryItem: string) => {
								return isNullOrWhitespace(commentaryItem);
							});

							// Only populate if item.commentary has valid commentary text array
							if (!isBlankCommentary) {
								commentaries.push(item);
							}
						}
                    }
                }
            }
        }
        // Display rule #2
        commentaries.sort((a, b) => {
            return a.order - b.order
        });

        return commentaries;
	}

	/**
	 * Function used to render each share class commentary object
	 *
	 * @param {*} commentaryObject the commentary object
	 * @param {*} index the current index
	 * @returns
	 * @memberof PageCommentary
	 */
	getCommentaryListItem(commentaryObject, index) {
		return (
			<span key={index}>
					{/* Display rule #5 in getMonthlyFundCommentary */}
					<h3 style={{ color: "#004f5c",  marginTop:"0"}} dangerouslySetInnerHTML={{ __html: commentaryObject.heading}}></h3>
					{/* Display rule #6 in getMonthlyFundCommentary */}
					<p dangerouslySetInnerHTML={ { __html: commentaryObject.commentary}}></p>
			</span>
		)
	}
	
	/**
	 * Function used to coordinate the rendering of the fund
	 * share class commentary
	 *
	 * @param {any[]} monthlyFundCommentary the share class commentary array
	 * @param {*} internalClasses the style classes object
	 * @returns
	 * @memberof PageCommentary
	 */
	renderMonthlyCommentary(monthlyFundCommentary, internalClasses) {
		// Check if there is commentary for the share class
		let { appParams } = this.props;
		let commentaryExists = (monthlyFundCommentary && (monthlyFundCommentary.length !== 0));

		let clientType = safe(() => appParams.clienttype, null);

		// If the share class has fund commentary, display all else display the default message
		if (commentaryExists) {
			//MG-1442: Hide "As of date' on Monthly Commentary component for End Investor (pri) only
			let dateAsAt = "";
			let formattedAsAtDate = null;
			if (clientType.indexOf('pri') < 0) {
				formattedAsAtDate = moment(monthlyFundCommentary[0].date).format("MMMM YYYY");
				dateAsAt = `As at: ${formattedAsAtDate}`;
			}

			// Based on the limitTo, show the first commentary otherwise show all
			let commentaryListing = null;
			if (this.state.limitTo === 1) {
				commentaryListing = this.getCommentaryListItem(monthlyFundCommentary[0], 0);
			} else {
				commentaryListing = monthlyFundCommentary.map((commentary, index) => {
					return this.getCommentaryListItem(commentary, index)
				});
			}
			return (
				<div className={styles.bodyText}>
					{<p className={styles.dateText}> {dateAsAt}</p>}
					<div>{commentaryListing}</div>
					<div>
						<div onClick={this.toggle} className={internalClasses.readMore}>
							<button type="button" className={internalClasses.readMoreBtn}> 
								<div className={internalClasses.caret}><FA_ANGLE_DOWN /></div>
							</button>
						</div>
					</div>
				</div>
			)
		} else {
			return (
				<div className={styles.bodyText}>
					<br />
					<p>There is currently no monthly commentary available for this fund.</p>
				</div>
			)
		}
	}

    render() {
		// Get the current share class
		let { shareClass, appParams } = this.props;
		// Get the read more visibility state
        let visibility = !this.state.visible ? '' : styles.active;
		// Build the style classes object
        let internalClasses = {
            caret: `${styles.caret} ${visibility}`,
            dropdown: `${styles.bodyTextHidden} ${visibility}`,
            elipsis: `${styles.elipsis} ${visibility}`,
            readMore: `${styles.readMore} ${visibility}`,
            readMoreBtn: `${styles.readMoreBtn} ${visibility}`
        }
		// Get the monthly fund / share class commentary
		let monthlyFundCommentary = this.getMonthlyFundCommentary(shareClass, appParams);		
        return (
            <div className={styles.wrapper}>
                <div className={styles.bodyRow}>
                    <h2 className={styles.heading}>MONTHLY FUND COMMENTARY</h2>
                    {this.renderMonthlyCommentary(monthlyFundCommentary, internalClasses)}
                </div>
            </div>
        )
    }
}




// WEBPACK FOOTER //
// ./src/client/components/PageCommentary/index.tsx